import { t } from "web-utils-strings/dist/index.js";
import { en, ar } from "../lang";
import { config } from "../config/config";
import { getSdk } from "./sdkService";
import { network } from "web-utils-network";
import localConfig from "../config/localConfig";
import { isForceLogout } from "../utils/isForceLogout";
import { getAppState } from "../state";

let currentLocale;
let defaultLocale;
const translations = {
  en,
  ar
};

export const directions = {
  LEFT_TO_RIGHT: "ltr",
  RIGHT_TO_LEFT: "rtl"
};

function getText(key, newLocale) {
  let text = findKeyInLocale(key, newLocale);
  if (!text && newLocale !== defaultLocale) {
    text = findKeyInLocale(key, defaultLocale);
  }
  return text;
}

export const i18n = {
  async init(defaultLanguage = config.get().sdk.defaultLocale, currentLanguage) {
    defaultLocale = defaultLanguage;
    currentLocale = currentLanguage || defaultLanguage;
    try {
      this.setLocale(currentLocale);
      return true;
    } catch (e) {
      return false;
    }
  },
  /**
   * Performs a network call to get a remote translation file corresponding to a given language.
   * @param {string} language A 2 character language code corresponding to the language of the translation file that
   * should be requested.
   * @returns {Promise<void>} A promise that resolves when the language file has been downloaded. The resolved promise
   * will not return the translation file itself. Instead, it will be available to use through the i18n service.
   */
  async requestLanguageFile(language = defaultLocale) {
    if (config.get().featureToggles.remoteLocales) {
      try {
        const response = await network.get(`${localConfig.languageFilesUrl}/${language}.json`);
        translations[language] = response.data;
      } catch (e) {
        console.log(`Could not download language file for ${language}. Falling back to local version.`);
      }
    }
  },
  getDirection() {
    currentLocale = currentLocale || config.get().sdk.defaultLocale;
    const locales = config.get().locales;
    return locales[currentLocale].direction;
  },
  directionFlip(left, right) {
    return this.getDirection() === directions.RIGHT_TO_LEFT ? right : left;
  },
  getLocale() {
    return currentLocale;
  },
  setLocale(locale) {
    currentLocale = locale;
    updateDom(locale);
    if (localStorage) {
      localStorage.setItem("locale", locale);
    }
  },
  /**
   * Looks for a translation text inside the currently active language. If the current locale doesn't have the requested
   * text, the function will try to find it in the default locale and if it is not there either, the key itelf will be
   * returned.
   * @param {string} key dot separated string that denotes the hierarchy to follow inside the translation file. For
   * example: "globals.appTitle"
   * @param {Array<string>} params  A list of strings that will be inserted in appearance order inside the text that
   * belongs to the key. This is useful to inject dynamic values in translations that require it.
   * @returns {String}  The found text with the injected dynamic fragments if any. In case the current locale doesn't
   * have the key, the default locale will be used instead. If still the key doesn't exist, the key itself will be
   * returned.
   */
  get(key, ...params) {
    const text = getText(key, currentLocale);
    return t(text, ...params) || key;
  },

  getWithLocale(key, newLocale = defaultLocale, ...params) {
    const text = getText(key, newLocale);
    return t(text, ...params) || key;
  },
  directions
};

/**
 * Looks in the translation file of the given language and looks for the key that corresponds to the received key. The
 * received key will be a dot separated string that denotes the hierarchy to follow inside the translation file.
 * @param {string} key dot separated string that denotes the hierarchy to follow inside the translation file. For
 * example: "globals.appTitle"
 * @param {string} locale A 2 character language code corresponding to the language of the translation file that should
 * be used to find the text.
 * @returns {string|boolean}  The text corresponding to the received key or false if the text could not be found in the
 * translation file.
 */
function findKeyInLocale(key, locale) {
  const propertyPath = key.split(".");
  let text = translations[locale];
  for (let i = 0; i < propertyPath.length; i++) {
    const property = propertyPath[i];
    text = text[property];
    if (!text) {
      return false;
    }
  }
  return text;
}

/**
 * Updates the lang and dir properties of the <html> tag in the page with the data of the given locale.
 * @param locale
 */
function updateDom(locale = config.get().sdk.defaultLocale || defaultLocale) {
  if (document && document.documentElement) {
    document.documentElement.setAttribute("lang", locale);
    document.documentElement.setAttribute("dir", config.get().locales[locale].direction);
  }
}
